import React, { useState } from 'react';
import { copyToClipboard } from '@/shared/utils/exportUtils';

export default function AdminDatabaseSection({ appId, showToast }) {
  const [expandedSections, setExpandedSections] = useState({
    drizzle: false,
    neon: false,
    drizzleNeonVercel: false
  });
  const [copiedCommand, setCopiedCommand] = useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCopy = async (command, label) => {
    const result = await copyToClipboard(command);
    if (result.success) {
      setCopiedCommand(label);
      if (showToast) {
        showToast(`Copied: ${command}`, 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy command', 'error');
      }
    }
  };

  const drizzleSteps = [
    {
      step: 1,
      title: 'Add to build script',
      command: 'npm run db:migrate',
      description: 'Add this command to your build script in package.json'
    },
    {
      step: 2,
      title: 'Update schema',
      command: 'Modify drizzle/schema.js',
      description: 'Tell Cursor to modify the drizzle/schema.js file to update the database schema'
    },
    {
      step: 3,
      title: 'Generate migration',
      command: 'npm run db:generate',
      description: 'Manually run this command to generate the SQL migration file'
    },
    {
      step: 4,
      title: 'Commit and push',
      command: 'git commit && git push',
      description: 'Commit the migration files and push to git'
    }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>🗄️</span>
          <span>Database</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Database configuration and migration workflow
        </p>
      </div>

      <div className="space-y-4">
        {/* Drizzle Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('drizzle')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Drizzle ORM</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.drizzle ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.drizzle && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-600 mb-4">
                Follow these steps to manage your database schema with Drizzle:
              </p>
              <div className="space-y-3">
                {drizzleSteps.map(({ step, title, command, description }) => (
                  <div
                    key={step}
                    className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex-shrink-0 w-6 h-6 bg-zapt-purple/20 text-zapt-purple rounded-full flex items-center justify-center text-xs font-semibold">
                      {step}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-1">{title}</p>
                      <p className="text-xs text-gray-500 mb-2">{description}</p>
                      {command && (
                        <div className="flex items-center justify-between gap-2">
                          <code className="text-xs text-gray-800 font-mono bg-gray-50 px-2 py-1 rounded border border-gray-300 flex-1 break-all">
                            {command}
                          </code>
                          {command.includes('npm') || command.includes('git') ? (
                            <button
                              onClick={() => handleCopy(command, `step-${step}`)}
                              className="px-2 py-1 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700 transition-colors flex items-center gap-1 flex-shrink-0"
                            >
                              {copiedCommand === `step-${step}` ? (
                                <>
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Neon Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('neon')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Neon</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.neon ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.neon && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <div className="space-y-2 text-sm text-gray-700">
                <p className="text-xs text-gray-600 mb-3">
                  Neon is a serverless Postgres database platform. Create and manage your database instance through the Neon console.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://console.neon.tech/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                  >
                    Open Neon Console →
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Drizzle + Neon on Vercel */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('drizzleNeonVercel')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Drizzle + Neon DB on Vercel</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.drizzleNeonVercel ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.drizzleNeonVercel && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-600 mb-4">
                Using the official Neon Serverless driver with Drizzle ORM on Vercel. The HTTP adapter is recommended for serverless environments — each query is a separate fetch request with low latency. See <a href="https://neon.com/docs/guides/drizzle" target="_blank" rel="noopener noreferrer" className="text-zapt-purple hover:underline">Neon's Drizzle guide</a> for full documentation.
              </p>

              {/* Section 1: Configuration */}
              <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                  1. Configuration
                </h5>
                <div className="mb-2">
                  <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-300 overflow-x-auto">
{`import { neon } from '@neondatabase/serverless';
import { drizzle } from 'drizzle-orm/neon-http';

const sql = neon(process.env.DATABASE_URL);
export const db = drizzle({ client: sql });`}
                  </pre>
                </div>
                <p className="text-xs text-gray-600">
                  Uses the <a href="https://www.npmjs.com/package/@neondatabase/serverless" target="_blank" rel="noopener noreferrer" className="text-zapt-purple hover:underline">@neondatabase/serverless</a> driver with Drizzle's <code className="bg-gray-100 px-1 py-0.5 rounded">neon-http</code> adapter, optimized for serverless/edge deployment.
                </p>
              </div>

              {/* Section 2: Include schema in Vercel functions */}
              <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                  2. Include schema in Vercel functions (vercel.json)
                </h5>
                <div className="mb-2">
                  <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-300 overflow-x-auto">
{`"functions": {
  "api/**/*.js": {
    "includeFiles": "drizzle/schema.js"
  }
}`}
                  </pre>
                </div>
                <p className="text-xs text-gray-600">
                  <strong>Why:</strong> Vercel serverless functions are bundled independently. Without this, the <code className="bg-gray-100 px-1 py-0.5 rounded">drizzle/schema.js</code> file isn't included in the function bundle, causing "module not found" errors at runtime.
                </p>
              </div>

              {/* Dependencies */}
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">Dependencies</h5>
                <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-300 overflow-x-auto">
{`"@neondatabase/serverless": "^1.0.0",
"drizzle-orm": "^0.45.1"`}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

